import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaBell } from 'react-icons/fa';
import { discoveryService, accountSettingsService } from '../../services/apiService';
import { useAuth } from '../../auth/AuthContext';
import { useVehicle } from '../../contexts/VehicleContext';
import CatalogNodeSeo from './CatalogNodeSeo';
import SellerRowCard from './SellerRowCard';
import { SORT_OPTIONS, sortCatalogListings } from './utils/catalogNodeSort';

const CONDITION_TAB_FALLBACK = [
  { key: 'new_oem', label: 'New OEM' },
  { key: 'new_aftermarket', label: 'New Aftermarket' },
  { key: 'used_grade_a', label: 'Used Grade A' },
  { key: 'used_grade_b', label: 'Used Grade B' },
  { key: 'used_grade_c', label: 'Used Grade C' },
];

const PAGE_SIZE = 10;

function readSsrInitialState() {
  if (typeof window === 'undefined') return null;
  const data = window.__CATALOG_NODE_INITIAL__;
  if (data && typeof data === 'object') {
    delete window.__CATALOG_NODE_INITIAL__;
    return data;
  }
  return null;
}

function vehiclePathLabel(node) {
  const v = node?.vehiclePath || node?.vehicle || node;
  if (!v) return '';
  const parts = [v.make, v.model, v.generation || v.year];
  if (v.variant && v.variant !== 'ALL') parts.push(v.variant);
  if (v.fuelType && v.fuelType !== 'ALL') parts.push(v.fuelType);
  return parts.filter(Boolean).join(' · ');
}

function buildVehicleCatalogPath(vehicle) {
  if (!vehicle) return '/catalog';
  const qs = new URLSearchParams();
  if (vehicle.make) qs.set('vehicleMake', vehicle.make);
  if (vehicle.model) qs.set('vehicleModel', vehicle.model);
  if (vehicle.year) qs.set('vehicleYear', String(vehicle.year));
  if (vehicle.variant) qs.set('vehicleVariant', vehicle.variant);
  if (vehicle.fuelType) qs.set('vehicleFuelType', vehicle.fuelType);
  const s = qs.toString();
  return s ? `/catalog?${s}` : '/catalog';
}

export default function CatalogNodePage() {
  const { catalogNodeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { selectedVehicle } = useVehicle();

  const vehicleId =
    searchParams.get('vehicleId') ||
    selectedVehicle?._id ||
    selectedVehicle?.id ||
    '';
  const buyerPincode = searchParams.get('pincode') || searchParams.get('buyerPincode') || '';
  const vehicleCatalogPath = buildVehicleCatalogPath(selectedVehicle);

  const ssrInitial = useMemo(() => readSsrInitialState(), []);

  const [loading, setLoading] = useState(!ssrInitial);
  const [page, setPage] = useState(ssrInitial);
  const [activeTab, setActiveTab] = useState(ssrInitial?.activeCondition || null);
  const [sortKey, setSortKey] = useState('default');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [notifyLoadingId, setNotifyLoadingId] = useState(null);

  const fetchPage = useCallback(async () => {
    if (!catalogNodeId) return;
    setLoading(true);
    try {
      const res = await discoveryService.getCatalogNodePage(catalogNodeId, {
        pincode: buyerPincode || undefined,
      });
      const data = res?.data || res;
      setPage(data);
      setActiveTab(
        (prev) =>
          prev ||
          data?.activeCondition ||
          data?.conditionTabs?.find((t) => t.hasStock)?.key ||
          CONDITION_TAB_FALLBACK[0].key
      );
    } catch (err) {
      setPage(null);
    } finally {
      setLoading(false);
    }
  }, [catalogNodeId, buyerPincode]);

  useEffect(() => {
    if (ssrInitial) {
      setActiveTab(
        ssrInitial.activeCondition ||
          ssrInitial.conditionTabs?.find((t) => t.hasStock)?.key ||
          CONDITION_TAB_FALLBACK[0].key
      );
      return;
    }
    fetchPage();
  }, [catalogNodeId, ssrInitial, fetchPage]);

  // Missing multi-seller node → marketplace parts for the selected vehicle.
  useEffect(() => {
    if (loading || page || !selectedVehicle?.make) return;
    const timer = window.setTimeout(() => {
      navigate(vehicleCatalogPath, { replace: true });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [loading, page, selectedVehicle?.make, vehicleCatalogPath, navigate]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setSortKey('default');
  }, [activeTab]);

  const conditionTabs = useMemo(() => {
    if (Array.isArray(page?.conditionTabs) && page.conditionTabs.length) {
      return page.conditionTabs;
    }
    return CONDITION_TAB_FALLBACK.map((t) => ({ ...t, stockCount: 0, totalCount: 0, hasStock: false }));
  }, [page]);

  const rawListings = useMemo(() => {
    if (!page) return [];
    if (page.listingsByCondition && activeTab) {
      return page.listingsByCondition[activeTab] || [];
    }
    return page.activeListings || page.listings || [];
  }, [page, activeTab]);

  const sortedListings = useMemo(
    () => sortCatalogListings(rawListings, sortKey),
    [rawListings, sortKey]
  );

  const visibleListings = sortedListings.slice(0, visibleCount);
  const hasMore = sortedListings.length > visibleCount;

  const handleNotify = async (listing) => {
    if (!isAuthenticated) {
      toast.info('Log in to get notified when this part is back in stock');
      navigate('/login');
      return;
    }
    const key = listing?.listingId || 'node';
    setNotifyLoadingId(key);
    try {
      await accountSettingsService.addToWishlist({
        partName: page?.partCategory || listing?.partName || 'Part',
        catalogNodeId: page?.catalogNodeId || catalogNodeId,
        listingId: listing?.listingId,
        nodeId: catalogNodeId,
        vehicleId,
        condition: listing?.condition || activeTab,
        notifyWhenAvailable: true,
      });
      toast.success('Saved — we will WhatsApp you when this part is restocked');
    } catch (err) {
      toast.error(err.message || 'Could not save alert');
    } finally {
      setNotifyLoadingId(null);
    }
  };

  if (loading && !page) {
    return (
      <div className="min-h-screen bg-[#070b12] px-4 py-12 text-center text-slate-400">
        Loading catalog node…
      </div>
    );
  }

  if (!page) {
    const vehicleLabel = selectedVehicle
      ? [selectedVehicle.make, selectedVehicle.model, selectedVehicle.year]
          .filter(Boolean)
          .join(' · ')
      : '';
    return (
      <div className="min-h-screen bg-[#070b12] px-4 py-12 text-center">
        <p className="mb-2 text-lg font-semibold text-slate-100">
          This catalog node isn&apos;t available yet
        </p>
        <p className="mx-auto mb-6 max-w-md text-sm text-slate-400">
          {vehicleLabel
            ? `Redirecting to marketplace parts for ${vehicleLabel}…`
            : 'This part isn\'t linked in the multi-seller catalog yet. Browse marketplace listings or search by part name.'}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to={vehicleCatalogPath}
            className="rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-500"
          >
            Browse catalog
          </Link>
          <Link
            to={vehicleId ? `/search?vehicleId=${vehicleId}` : '/search'}
            className="rounded-xl border border-slate-600 bg-slate-900/80 px-5 py-2.5 text-sm font-semibold text-slate-200 hover:border-slate-400"
          >
            Search parts
          </Link>
        </div>
      </div>
    );
  }

  const nodeStatus = page.status || page.nodeStatus;
  const fitment = vehiclePathLabel(page);
  const availableInTab = rawListings.filter((l) => l.available !== false).length;

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <CatalogNodeSeo page={page} catalogNodeId={catalogNodeId} />

      <div className="max-w-5xl mx-auto px-4 py-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <FaArrowLeft /> Back
        </button>

        <header className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm mb-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 mb-1">
                Multi-seller catalog node
              </p>
              <h1 className="text-2xl font-bold text-slate-900">{page.partCategory}</h1>
              {fitment && (
                <p className="text-sm text-slate-600 mt-1">
                  {fitment}
                </p>
              )}
              {page.subSystem && (
                <p className="text-xs text-slate-500 mt-1">Sub-system: {page.subSystem}</p>
              )}
              {page.gapPending && (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  This part was requested and is queued for catalog creation
                  {page.gapDueBy
                    ? ` (due by ${new Date(page.gapDueBy).toLocaleDateString()})`
                    : ''}
                  . We&apos;ll notify you when sellers are available.
                </p>
              )}
            </div>
            <span
              className={`text-xs font-bold uppercase px-3 py-1 rounded-full ${
                nodeStatus === 'active' && availableInTab > 0
                  ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-amber-100 text-amber-800'
              }`}
            >
              {availableInTab > 0
                ? `${availableInTab} seller${availableInTab === 1 ? '' : 's'}`
                : 'No stock'}
            </span>
          </div>
        </header>

        <div
          className="flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1 mb-4"
          role="tablist"
          aria-label="Part condition"
        >
          {conditionTabs.map((tab) => {
            const count = tab.stockCount ?? 0;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 min-w-[100px] rounded-lg py-2.5 px-2 text-[10px] sm:text-xs font-bold uppercase transition ${
                  isActive
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <span className="block leading-tight">{tab.label}</span>
                {count > 0 && (
                  <span className="block text-[9px] font-normal normal-case text-emerald-600 mt-0.5">
                    {count} available
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {sortedListings.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-xs text-slate-500">
              Sorted by combined score: price 40% · rating 35% · dispatch 25%
            </p>
            <label className="flex items-center gap-2 text-sm">
              <span className="text-slate-500 shrink-0">Sort:</span>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              >
                {(page.sortOptions || SORT_OPTIONS).map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {sortedListings.length > 0 ? (
          <>
            <ul className="space-y-3" aria-label="Seller listings">
              {visibleListings.map((listing) => (
                <li key={listing.listingId}>
                  <SellerRowCard
                    listing={listing}
                    catalogNodeId={catalogNodeId}
                    vehicleId={vehicleId}
                    activeTab={activeTab}
                    onNotify={handleNotify}
                    notifyLoading={notifyLoadingId === listing.listingId}
                  />
                </li>
              ))}
            </ul>

            {hasMore && (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="rounded-full border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 hover:border-indigo-400 hover:text-indigo-700"
                >
                  Show more sellers ({sortedListings.length - visibleCount} remaining)
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
            <p className="text-slate-600 mb-4">No listings in this condition.</p>
            <button
              type="button"
              onClick={() => handleNotify({})}
              disabled={notifyLoadingId === 'node'}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
            >
              <FaBell /> Notify me when available
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
